'use client';

import React, { useState } from 'react';
import { MessageCircle, X, ChevronRight } from 'lucide-react';
import type { EventData } from '../types/events';
import { buildWhatsAppUrl } from './CarteleraClient';

interface SmileEventCardProps {
  event: EventData;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  openListaFree?: (event: EventData) => void;
}

export default function SmileEventCard({ event }: SmileEventCardProps) {
  const [showOptions, setShowOptions] = useState(false);

  const handleSmileWA = (optionLabel: string) => {
    const msg = `Hola Benja! Quiero consultar por ${optionLabel} para Smile Matinée - SALAS (Sáb 26/09) 🙌`;
    window.open(buildWhatsAppUrl(msg), '_blank');
    setShowOptions(false);
  };

  return (
    <>
      {/* Horizontal Row Card */}
      <div
        className={`${event.gradientClass} card-shine rounded-2xl overflow-hidden transition-all duration-300 w-full`}
        style={{
          border: `1.5px solid ${event.borderColor}`,
          boxShadow: `0 0 18px ${event.glowColor}, 0 0 4px ${event.glowColor}`,
          '--card-glow': event.glowColor,
        } as React.CSSProperties}
        onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.boxShadow = `0 0 30px ${event.glowColor}, 0 0 60px ${event.glowColor}`; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.boxShadow = `0 0 18px ${event.glowColor}, 0 0 4px ${event.glowColor}`; }}
        onTouchStart={(e) => { (e.currentTarget as HTMLDivElement).style.boxShadow = `0 0 30px ${event.glowColor}, 0 0 60px ${event.glowColor}`; }}
        onTouchEnd={(e) => { (e.currentTarget as HTMLDivElement).style.boxShadow = `0 0 18px ${event.glowColor}, 0 0 4px ${event.glowColor}`; }}
      >
        <div className="flex items-center gap-0 p-0">
          {/* LEFT — Date block */}
          <div className="flex flex-col items-center justify-center px-3 sm:px-4 py-4 sm:py-5 min-w-[64px] sm:min-w-[76px] bg-black/30 self-stretch">
            <span className="text-[9px] sm:text-[10px] font-bold text-white/60 uppercase tracking-widest leading-none mb-1">
              {event.dayLabel.slice(0, 3).toUpperCase()}
            </span>
            <span
              className="text-3xl sm:text-4xl font-black text-white leading-none"
              style={{ textShadow: `0 0 10px ${event.accentColor}` }}
            >
              {event.dateLabel.split('/')[0]}
            </span>
            <span className="text-[9px] sm:text-[10px] font-semibold text-white/50 leading-none mt-1">
              /{event.dateLabel.split('/')[1]}
            </span>
          </div>

          {/* CENTER — Event info */}
          <div className="flex-1 px-3 sm:px-4 py-3 sm:py-4 min-w-0">
            <h3
              className="text-sm sm:text-base font-extrabold text-white leading-tight"
              style={{ textShadow: `0 0 8px ${event.accentColor}`, wordBreak: 'break-word' }}
            >
              {event.title}
            </h3>
            <p className="text-xs sm:text-sm text-white/80 mt-0.5 leading-snug">{event.subtitle}</p>
            {event.timeRange && (
              <p className="text-xs text-white/60 mt-1.5">⏰ {event.timeRange}</p>
            )}
            <div
              className="mt-2 rounded-lg px-2 py-1.5 text-[11px] font-medium text-center"
              style={{
                background: `rgba(0,0,0,0.35)`,
                border: `1px solid ${event.borderColor}`,
                color: event.accentColor,
                textShadow: `0 0 6px ${event.accentColor}`,
              }}
            >
              💬 Todos los ingresos se gestionan por WhatsApp
            </div>
          </div>

          {/* RIGHT — Single neon button */}
          <div className="flex items-center justify-center px-2 sm:px-3 py-3 sm:py-4 self-stretch">
            <button
              onClick={() => setShowOptions(true)}
              className="flex flex-col items-center justify-center gap-1 rounded-xl font-bold text-[10px] sm:text-[11px] text-center leading-tight active:scale-95 transition-all"
              style={{
                background: `linear-gradient(135deg, rgba(0,0,0,0.5), rgba(0,0,0,0.3))`,
                border: `1.5px solid ${event.accentColor}`,
                boxShadow: `0 0 16px ${event.glowColor}, 0 0 30px ${event.glowColor}, inset 0 0 10px rgba(255,255,255,0.05)`,
                color: event.accentColor,
                textShadow: `0 0 8px ${event.accentColor}`,
                minWidth: '64px',
                padding: '10px 8px',
              }}
            >
              <ChevronRight size={16} style={{ color: event.accentColor }} />
              <span>TOCÁ</span>
              <span>PARA VER</span>
              <span>OPCIONES</span>
            </button>
          </div>
        </div>
      </div>

      {/* Options Modal */}
      {showOptions && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 modal-backdrop"
          onClick={(e) => { if (e.target === e.currentTarget) setShowOptions(false); }}
        >
          <div className="bg-card w-full sm:max-w-sm sm:rounded-2xl rounded-t-2xl border border-border animate-slide-up overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <div>
                <h3 className="font-extrabold text-sm text-foreground">{event.title}</h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {event.dayLabel} {event.dateLabel} · Elegí una opción
                </p>
              </div>
              <button
                onClick={() => setShowOptions(false)}
                className="p-2 rounded-xl hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
              >
                <X size={18} />
              </button>
            </div>

            {/* Options — WhatsApp buttons */}
            <div className="p-4 flex flex-col gap-2">
              {(event.smileButtons || []).map((btn) => (
                <button
                  key={`smile-modal-btn-${btn.id}`}
                  onClick={() => handleSmileWA(btn.label)}
                  className="btn-whatsapp w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold text-white active:scale-95 transition-all"
                >
                  <span>{btn.emoji}</span>
                  <span>{btn.label}</span>
                  <MessageCircle size={14} className="ml-auto opacity-70" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}